import axios from "axios";

/**
 * Origine API optionnelle (ex: https://api.example.com).
 * Laisser vide en prod derrière nginx qui proxy /api → backend (recommandé).
 */
export const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN || "").replace(/\/$/, "");

/** Construit une URL API absolue ou relative selon la config. */
export function apiUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${API_ORIGIN}${p}`;
}

const API = axios.create({
  baseURL: apiUrl("/api"),
  withCredentials: true,
});

let refreshPromise: Promise<void> | null = null;

async function refreshAccessCookie(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(apiUrl("/api/auth/refresh"), null, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config as
      | (typeof error.config & { _retry?: boolean })
      | undefined;
    const status = error?.response?.status;
    const url = String(original?.url ?? "");
    const skipRefresh =
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");

    if (status === 401 && original && !original._retry && !skipRefresh) {
      original._retry = true;
      try {
        await refreshAccessCookie();
        return API(original);
      } catch {
        localStorage.removeItem("vulnix_token");
      }
    }
    return Promise.reject(error);
  },
);

export interface ScanRequest {
  repoUrl: string;
  branch?: string;
  /** Scan this exact commit (full SHA); the scan fails if the commit is not found. */
  commitSha?: string;
  scanMode?: string;
  targetDomain?: string;
  dastTargetUrl?: string;
  dockerImage?: string;
  containerPort?: number;
  /** Idée 3 — OS cible : e.g. "ubuntu:22.04", "alpine:3.18", "windows:2022" */
  targetOs?: string;
  /** Idée 2 — OpenSCAP compliance profile: "CIS_L1", "CIS_L2", "NIST_800-53", "PCI_DSS" */
  complianceProfile?: string;
}

export interface ScanResponse {
  scanId: number;
  repoId: number;
}

export interface CveDto {
  id: number;
  cveId: string;
  canonicalId?: string | null;
  aliases?: string | null;
  findingKind?: string | null;
  cweId?: string | null;
  target?: string | null;
  fixAvailable?: boolean;
  priorityLabel?: string | null;
  packageName: string;
  packageVersion: string;
  severity: string;
  cvssScore: number | null;
  fixedVersion: string | null;
  description: string | null;
  dataSource: string | null;
  source: string;
  filePath: string | null;
  lineNumber: number | null;
  exploitAvailable: boolean;
  exploitUrl: string | null;
  kevListed: boolean;
  kevDateAdded: string | null;
  kevRansomware: boolean;
  epssScore: number | null;
  epssPercentile: number | null;
  confirmedBy: number;
  sources: string | null;
  /** Idée 3 — OS cible : "WINDOWS", "LINUX", "CROSS_PLATFORM" (from NVD CPE data) */
  affectedOs: string | null;
  // ── SBOM enrichment fields ──────────────────────────────────────────────
  /** Exact component name from SBOM */
  componentName: string | null;
  /** Exact component version from SBOM */
  componentVersion: string | null;
  /** Component type: "library", "framework", "container", etc. */
  componentType: string | null;
  /** Package ecosystem: "npm", "maven", "pypi", "golang", etc. */
  ecosystem: string | null;
  /** Package manager: "npm", "mvn", "pip", "cargo", etc. */
  packageManager: string | null;
  /** Dependency scope: "runtime", "dev", "test", "optional", "unknown" */
  dependencyScope: string | null;
  /** "DIRECT", "TRANSITIVE", or "UNKNOWN" */
  directOrTransitive: string | null;
  /** Depth from project root (1 = direct) */
  dependencyDepth: number | null;
  /** Human-readable dependency path e.g. "frontend-rh → axios → follow-redirects" */
  dependencyPath: string | null;
  /** Does the project's own code import this package? YES | TEST_ONLY | NOT_IMPORTED | UNKNOWN */
  codeUsage?: string | null;
  usageFiles?: string[];
  /** Package URL e.g. "pkg:npm/axios@0.21.1" */
  purl: string | null;
  /** BOM reference from the SBOM document */
  bomRef: string | null;
  /** Manifest file path e.g. "frontend-rh/package-lock.json" */
  manifestFile: string | null;
  /** Module name derived from manifestFile */
  moduleName: string | null;
  /** Confidence of the direct/transitive classification: "HIGH", "MEDIUM", "LOW" */
  dependencyConfidence: string | null;
}

export interface RepositoryDto {
  id: number;
  repoUrl: string;
  gitProvider?: string;
  branch: string;
  scanMode: string;
  targetDomain: string;
  clientIds?: number[];
  clientNames?: string[];
  createdAt: string;
  lastScannedAt: string;
}

export interface ScanResultDto {
  id: number;
  repoId: number;
  repoUrl: string;
  gitProvider?: string;
  branch: string;
  scanMode: string;
  targetDomain: string;
  clientIds?: number[];
  clientNames?: string[];
  status: string;
  startedAt: string;
  finishedAt: string;
  ecosystemsDetected: string;
  toolsExecuted: string;
  cveCount: number;
  secretCount: number;
}

export interface SecretDto {
  id: number;
  ruleId: string;
  description: string;
  file: string;
  startLine: number;
  endLine: number;
  author: string;
  date: string;
  commit: string;
  maskedMatch: string | null;
  /** Triage by the ML model: LIKELY_REAL | REVIEW | LIKELY_FALSE_POSITIVE; absent when not triaged yet. */
  /** Detectors that reported it: gitleaks and/or credsweeper. */
  detectors?: string[];
  mlVerdict?: string | null;
  mlProba?: number | null;
  /** PRODUCTION | TEST_OR_DOC */
  mlScope?: string | null;
  mlPriority?: number | null;
  mlReasons?: string[];
  /** The analyst's call, which overrides the model: REAL | FALSE_POSITIVE. */
  analystVerdict?: string | null;
}

// Start a scan
export const startScan = (data: ScanRequest) =>
  API.post<ScanResponse>("/scans", data);

// Get CVEs for a scan
export const getCvesByScan = (scanId: number) =>
  API.get<CveDto[]>(`/scans/${scanId}/cves`);

export const getKevStatus = () =>
  API.get<{ catalogSize: number; loaded: boolean }>("/kev/status");

// Get all repositories
export const getRepositories = () => API.get<RepositoryDto[]>("/repositories");

/** Saves a Git URL as a repository without launching a scan. */
export const registerRepository = (data: { repoUrl: string }) =>
  API.post<RepositoryDto>("/repositories", data);

// Get all scans
export const getAllScans = () => API.get<ScanResultDto[]>("/scans");

export interface ScanSeveritySummary {
  scanId: number;
  critical: number;
  high: number;
  kev: number;
}

export const getScanSeveritySummary = (ids: number[]) =>
  API.get<ScanSeveritySummary[]>("/scans/severity-summary", { params: { ids: ids.join(",") } });

// Get scan history for a repo
export const getScansByRepo = (repoId: number) =>
  API.get<ScanResultDto[]>(`/repositories/${repoId}/scans`);

// Compare two scans of the same repo: new / fixed / persisting CVEs & secrets
export interface ScanDiffDto {
  fromScanId: number;
  toScanId: number;
  newCves: CveDto[];
  fixedCves: CveDto[];
  persistingCves: CveDto[];
  newSecrets: SecretDto[];
  fixedSecrets: SecretDto[];
}

export const compareScans = (fromScanId: number, toScanId: number) =>
  API.get<ScanDiffDto>("/scans/compare", { params: { fromScanId, toScanId } });

// ── Scan workers (parallel Kali worker pool) ────────────────────────────
export interface ScanWorkerStatus {
  workerId: string;
  category: "ssl" | "code" | "image";
  busy: boolean;
  currentScanId: number | null;
}

export const getScanWorkers = () => API.get<ScanWorkerStatus[]>("/scans/workers");

// ── Attack graph ──────────────────────────────────────────────────────
export interface AttackGraphNodeDto {
  id: string;
  type: "REPO" | "SECRET" | "CVE" | "SERVER" | "HARDENING";
  label: string;
  severity: string | null;
  critical: boolean;
  meta: Record<string, unknown>;
}

export interface AttackGraphEdgeDto {
  id: string;
  source: string;
  target: string;
  kind: "CONTAINS" | "DEPLOYS" | "SECRET_ACCESS" | "RCE_CVE" | "HARDENING_AMPLIFIER";
  weight: number;
  label: string;
}

export interface AttackGraphResponseDto {
  nodes: AttackGraphNodeDto[];
  edges: AttackGraphEdgeDto[];
}

export interface AttackPathDto {
  id: string;
  nodeIds: string[];
  score: number;
  narrative: string;
}

export const getAttackGraph = (clientId?: number) =>
  API.get<AttackGraphResponseDto>("/attack-graph", { params: clientId ? { clientId } : undefined });

export const getAttackPaths = (targetServerId?: number, clientId?: number) =>
  API.get<AttackPathDto[]>("/attack-graph/paths", {
    params: { ...(targetServerId ? { targetServerId } : {}), ...(clientId ? { clientId } : {}) },
  });

// ── Domain intelligence: Certificate Transparency + DNS security ───────
export interface CertTransparencyEntryDto {
  subdomain: string;
  issuer: string;
  notBefore: string;
  notAfter: string;
  knownAsset: boolean;
}

export interface CertTransparencyResultDto {
  domain: string;
  subdomains: CertTransparencyEntryDto[];
  totalCertificates: number;
  unknownCount: number;
  status: "OK" | "ERROR";
}

export interface DnsSecurityResultDto {
  domain: string;
  spfRecord: string | null;
  spfPresent: boolean;
  dmarcRecord: string | null;
  dmarcPresent: boolean;
  dmarcPolicy: string | null;
  caaRecords: string[];
  caaStatus: "READY" | "NOT_TESTED";
}

export const getCertTransparency = (domain: string) =>
  API.get<CertTransparencyResultDto>("/ssl/cert-transparency", { params: { domain } });

export const getDnsSecurity = (domain: string) =>
  API.get<DnsSecurityResultDto>("/ssl/dns-security", { params: { domain } });

// Get CVEs from latest scan of a repo
export const getCvesByRepo = (repoId: number) =>
  API.get<CveDto[]>(`/repositories/${repoId}/cves`);

// Get secrets for a scan
export const getSecretsByScan = (scanId: number) =>
  API.get<SecretDto[]>(`/scans/${scanId}/secrets`);

// Run the secret-triage model again on the stored secrets of a scan
export const triageSecrets = (scanId: number) =>
  API.post<SecretDto[]>(`/scans/${scanId}/secrets/triage`, null, { timeout: 180000 });

// The analyst's decision on one secret (null clears it)
export const setSecretVerdict = (secretId: number, verdict: "REAL" | "FALSE_POSITIVE" | null) =>
  API.put<SecretDto>(`/secrets/${secretId}/verdict`, { verdict });

// Get SBOM components for a scan
export interface SbomComponent {
  id: string;
  name: string;
  version: string;
  type: string;
  language: string;
  purl: string;
  license: string;
  location: string;
}

export const getSbomByScan = (scanId: number) =>
  API.get<SbomComponent[]>(`/scans/${scanId}/sbom`);

// ── Supply chain screening of the scan's npm dependencies (indicator, never a gate) ──
export interface SupplyChainItem {
  ecosystem?: "npm" | "pypi" | string;
  name: string;
  version: string;
  tier: "KNOWN_MALICIOUS" | "HIGH_CONFIDENCE" | "REVIEW" | string;
  suspicionProba?: number | null;
  probableType?: string | null;
  reasons: string[];
  knownMaliciousId?: string | null;
  scoredAt?: string | null;
}

/** One analysed (or skipped) dependency, compact: the proof that the model really looked at it. */
export interface SupplyChainBrief {
  ecosystem?: string;
  name: string;
  version: string;
  status?: string;
  tier?: string | null;
  suspicionProba?: number | null;
}

export interface SupplyChainCheckResult {
  name: string;
  version?: string | null;
  scoredVersion?: string | null;
  ecosystem: string;
  status: string;
  tier?: string | null;
  suspicionProba?: number | null;
  probableType?: string | null;
  popularPackage?: boolean;
  knownMaliciousId?: string | null;
  reasons: string[];
  drivers?: { feature: string; label: string; impact: number }[];
}

export interface SupplyChainReport {
  scanId: number;
  enabled: boolean;
  modelAvailable: boolean;
  /** One model per ecosystem (npm, pypi); an ecosystem without its model is simply not screened. */
  modelsAvailable?: Record<string, boolean>;
  componentsByEcosystem?: Record<string, number>;
  modelVersion: string;
  analysisRunning: boolean;
  totalComponents: number;
  summary: {
    knownMalicious: number;
    highConfidence: number;
    review: number;
    low: number;
    notAnalysed: number;
    pending: number;
  };
  flagged: SupplyChainItem[];
  /** Every dependency the model scored, most suspicious first. */
  analyzed?: SupplyChainBrief[];
  /** Dependencies that could not be analysed (not on the registry, too large...). */
  skipped?: SupplyChainBrief[];
  disclaimer: string;
}

export const getSupplyChain = (scanId: number) =>
  API.get<SupplyChainReport>(`/scans/${scanId}/supply-chain`);

/** Score a few packages right now, nothing is stored: test a package before adding it to a project. */
export const checkSupplyChain = (packages: { ecosystem: string; name: string; version?: string }[]) =>
  API.post<SupplyChainCheckResult[]>("/supply-chain/check", { packages }, { timeout: 180000 });

export const analyzeSupplyChain = (scanId: number) =>
  API.post<SupplyChainReport>(`/scans/${scanId}/supply-chain/analyze`, null, { timeout: 600000 });


// Stop a running scan
export const stopScan = (scanId: number) => API.post(`/scans/${scanId}/stop`);

// Delete a scan
export const deleteScan = (scanId: number) => API.delete(`/scans/${scanId}`);

// ── Auto-Fix programmatique ───────────────────────────────────────────────────

export interface FixPreviewRequest {
  repoFullName: string;
  packageName: string;
  currentVersion: string;
  fixedVersion: string;
  cveId: string;
  filePath: string | null;
  source: string | null;
  provider?: "GITHUB" | "GITLAB";
  branch?: string | null;
}

export interface HumanKnowledgeDto {
  id: number;
  cveId?: string | null;
  packageName: string;
  ecosystem?: string | null;
  fromVersion?: string | null;
  toVersion?: string | null;
  filePath?: string | null;
  repoFullName?: string | null;
  reason: string;
  createdByLogin?: string | null;
  usageCount: number;
  successCount: number;
  failCount: number;
  status: string;
  createdAt?: string | null;
  lastUsedAt?: string | null;
}

export interface LlmFixAdvice {
  recommendation: "AI" | "HUMAN" | "NEEDS_REVIEW" | string;
  confidence: number;
  summary: string;
  why: string;
  risks?: string;
  humanStillValid?: boolean;
  source?: string;
}

export interface FixPreviewResponse {
  originalLines: string[];
  fixedLines: string[];
  fixedContent: string;
  filePath: string;
  sha: string;
  // npm lock file — also patched so the next scan doesn't flag the same CVE
  lockFilePath?: string | null;
  lockFileSha?: string | null;
  lockFileContent?: string | null;
  // Assisted-fix agent memory
  hasHumanKnowledge?: boolean;
  humanKnowledge?: HumanKnowledgeDto | null;
  humanFixedContent?: string | null;
  humanFixedLines?: string[] | null;
  llmAdvice?: LlmFixAdvice | null;
  // Sprint A — chef policy
  officialStableVersion?: string | null;
  officialComment?: string | null;
  policySource?: "CHEF" | "SCAN" | string | null;
  policyPreferredVersion?: string | null;
}

export interface FixApplyRequest {
  repoFullName: string;
  filePath: string;
  sha: string;
  fixedContent: string;
  commitMessage: string;
  provider?: "GITHUB" | "GITLAB";
  branch?: string | null;
  // optional lock file fields — when present, the lock file is also committed
  lockFilePath?: string | null;
  lockFileSha?: string | null;
  lockFileContent?: string | null;
  // Assisted-fix agent
  developerEdited?: boolean;
  memorize?: boolean;
  reason?: string | null;
  chosenSource?: "AI" | "HUMAN" | string;
  knowledgeId?: number | null;
  aiFixedContent?: string | null;
  packageName?: string | null;
  currentVersion?: string | null;
  fixedVersion?: string | null;
  cveId?: string | null;
  source?: string | null;
  riskAccepted?: boolean;
}

export interface FixApplyResponse {
  commitUrl?: string;
  sha?: string;
  knowledgeId?: number;
  knowledgeSaved?: boolean;
  status?: "COMMITTED" | "PENDING_APPROVAL" | string;
  requestId?: number;
  message?: string;
  proposedVersion?: string;
  officialVersion?: string;
  requestedByLogin?: string;
  chosenSource?: string;
}

export interface AppNotificationDto {
  id: number;
  type?: string | null;
  title?: string | null;
  message?: string | null;
  link?: string | null;
  relatedRequestId?: number | null;
  read?: boolean;
  createdAt?: string | null;
}

export interface PolicyDeviationDto {
  id: number;
  cveId?: string | null;
  packageName?: string | null;
  officialVersion?: string | null;
  proposedVersion?: string | null;
  currentVersion?: string | null;
  reason?: string | null;
  status?: string | null;
  requestedByLogin?: string | null;
  reviewedByLogin?: string | null;
  reviewComment?: string | null;
  repoFullName?: string | null;
  filePath?: string | null;
  branch?: string | null;
  commitUrl?: string | null;
  commitMessage?: string | null;
  errorMessage?: string | null;
  error?: string | null;
  commitFailed?: boolean;
  createdAt?: string | null;
  reviewedAt?: string | null;
}

export interface GitRepoDto {
  name: string;
  fullName: string;
  description: string;
  language: string;
  private?: boolean;
  isPrivate?: boolean;
  stars: number;
  htmlUrl: string;
  updatedAt: string;
  provider: "GITHUB" | "GITLAB" | string;
}

export interface UserDto {
  id: number;
  login: string;
  name: string;
  avatarUrl: string;
  email: string;
  role: string;
  systemRole: "ADMIN" | "EMPLOYEE" | string;
  accessRoleId?: number | null;
  accessRoleKey?: string | null;
  permissions: string[];
  suspended: boolean;
  /** Deployment / server alerts also by e-mail (the bell always gets them). */
  emailNotifications?: boolean;
  primaryProvider: "GITHUB" | "GITLAB" | "LOCAL" | string;
  hasGithubLinked: boolean;
  hasGitlabLinked: boolean;
  gitlabUrl?: string | null;
  hasLocalPassword: boolean;
  createdAt?: string;
  aiProvider?: string | null; // GEMINI | CLAUDE | OPENAI | GROK | GROQ | null
  aiModel?: string | null;
  hasCustomAiKey?: boolean;
  hasCustomChatAiKey?: boolean;
  chatAiProvider?: string | null;
  chatAiModel?: string | null;
}

export interface AccessRoleDto {
  id: number;
  roleKey: string;
  name: string;
  description: string | null;
  baseRole: "ADMIN" | "EMPLOYEE" | string;
  systemRole: boolean;
  permissions: string[];
}

export interface LocalLoginResponse {
  user: UserDto;
}

export interface ClientDto {
  id: number;
  name: string;
  company?: string;
  domainName?: string;
  email?: string;
  /** ISO date yyyy-MM-dd — client delivery date, not CISA / lab SLA. */
  releaseDeadline?: string | null;
  releaseLabel?: string | null;
  /** yyyy-MM-dd. Day the project asked to be warned about the certificate. */
  sslAlertDate?: string | null;
  /** yyyy-MM-dd. Certificate expiry from the latest SSL scan; only sent when an alert is set. */
  sslCertExpiresOn?: string | null;
  createdById?: number;
  createdByLogin?: string;
  employeeIds: number[];
  employeeLogins: string[];
  repositoryIds: number[];
  repositoryUrls: string[];
  /** Role of each repository inside this project. Missing entries mean OTHER. */
  repositoryRoles?: { repositoryId: number; role: "FRONT" | "BACK" | "DATABASE" | "OTHER" }[];
  createdAt?: string;
}

export interface CreateClientRequest {
  name: string;
  company?: string;
  domainName?: string;
  email?: string;
  releaseDeadline?: string | null;
  releaseLabel?: string | null;
}

export interface UpdateClientRequest {
  name?: string;
  company?: string;
  domainName?: string;
  email?: string;
  releaseDeadline?: string | null;
  releaseLabel?: string | null;
}

/** ISO date (yyyy-MM-dd) → French display, local calendar (no UTC day-shift). */
export function formatReleaseDeadline(iso?: string | null): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export interface CiTokenDto {
  id: number;
  name: string;
  tokenPrefix: string;
  clientId: number;
  clientName?: string;
  repositoryIds: number[];
  repositoryUrls: string[];
  scopes: string[];
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string;
  active: boolean;
  revealable?: boolean;
}

export interface CiTokenCreatedDto extends CiTokenDto {
  token: string;
}

export interface CreateCiTokenRequest {
  name: string;
  clientId: number;
  repositoryIds: number[];
  expiresInDays?: number;
}

export interface ServerNodeRequest {
  name: string;
  host: string;
  port: number;
  username: string;
  nodeType: string;
  authMethod: "PASSWORD" | "PRIVATE_KEY";
  environment?: string;
  templateKey?: string;
  owner?: string;
  clientName?: string;
  projectName?: string;
  runbookUrl?: string;
  tags?: string[];
  notes?: string;
  password?: string;
  privateKey?: string;
  privateKeyPassphrase?: string;
  description?: string;
  deployPath?: string;
  domain?: string;
  linkedRepositoryId?: number | null;
  deployBranch?: string;
  deployStrategy?: string;
}

export interface ServerNodeDto {
  id: number;
  name: string;
  host: string;
  port: number;
  username: string;
  nodeType: string;
  environment?: string | null;
  templateKey?: string | null;
  owner?: string | null;
  clientName?: string | null;
  projectName?: string | null;
  runbookUrl?: string | null;
  tags: string[];
  notes?: string | null;
  description?: string | null;
  lastScannedAt?: string | null;
  latestStatus?: string | null;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  osName?: string | null;
  kernelVersion?: string | null;
  firewallStatus?: string | null;
  deployPath?: string | null;
  domain?: string | null;
  linkedRepositoryId?: number | null;
  deployBranch?: string | null;
  deployStrategy?: string | null;
  autoDeployEnabled?: boolean;
}

export interface PortExposureDto {
  portNumber: number;
  protocol: string;
  bindAddress: string;
  processName: string;
  serviceName: string;
  exposureLevel: string;
  state: string;
}

export interface PortRecommendationDto {
  portNumber: number;
  protocol: string;
  serviceName: string;
  /** Explication du risque de sécurité (en français) */
  riskReason: string;
  /** Commande Linux pour désactiver ce port/service */
  disableCommand: string;
  /** CRITICAL | WARNING | INFO */
  severity: 'CRITICAL' | 'WARNING' | 'INFO' | string;
}

export interface ServiceStatusDto {
  serviceName: string;
  state: string;
  subState: string;
  enabledStatus: string;
}

export interface HardeningFindingDto {
  id: number;
  category: string;
  severity: "CRITICAL" | "WARNING" | "INFO" | string;
  title: string;
  description: string;
  recommendation: string;
  detectedValue?: string | null;
}

export interface ConfigSnapshotDto {
  id: number;
  status: string;
  collectedAt: string;
  summary: string;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  driftChanges: string[];
}

export interface ServerNodeDetailDto {
  id: number;
  name: string;
  host: string;
  port: number;
  username: string;
  nodeType: string;
  authMethod: string;
  environment?: string | null;
  templateKey?: string | null;
  owner?: string | null;
  clientName?: string | null;
  projectName?: string | null;
  runbookUrl?: string | null;
  tags: string[];
  notes?: string | null;
  description?: string | null;
  lastScannedAt?: string | null;
  latestStatus?: string | null;
  hostname?: string | null;
  osName?: string | null;
  kernelVersion?: string | null;
  cpuSummary?: string | null;
  memorySummary?: string | null;
  diskSummary?: string | null;
  firewallStatus?: string | null;
  sshRootLogin?: string | null;
  dockerSummary?: string | null;
  certificateSummary?: string | null;
  summary?: string | null;
  journalExcerpt?: string | null;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
  driftChanges: string[];
  ports: PortExposureDto[];
  services: ServiceStatusDto[];
  findings: HardeningFindingDto[];
  recentSnapshots: ConfigSnapshotDto[];
  deployPath?: string | null;
  domain?: string | null;
  linkedRepositoryId?: number | null;
  deployBranch?: string | null;
  deployStrategy?: string | null;
  autoDeployEnabled?: boolean;
}

export interface DeployFindingDto {
  cveId?: string | null;
  severity?: string | null;
  packageName?: string | null;
  packageVersion?: string | null;
}

export interface ServerDeploymentDto {
  id: number;
  serverId: number;
  name: string;
  deployPath?: string | null;
  domain?: string | null;
  linkedRepositoryId?: number | null;
  deployBranch?: string | null;
  deployStrategy?: string | null;
  autoDeployEnabled?: boolean;
  /** Checked from the server after each deploy; empty = every compose container must stay up. */
  healthUrl?: string | null;
  backupEnabled?: boolean;
  backupService?: string | null;
  approvalRequired?: boolean;
  lastStatus?: string | null;
  lastCommitSha?: string | null;
}

export interface DeployRunDto {
  id: number;
  serverId: number;
  deploymentId?: number | null;
  commitSha?: string | null;
  /** Green pipeline that started this deploy (trigger CI). */
  pipelineNumber?: number | null;
  verdict?: string | null;
  /** BLOCKED, RUNNING, SUCCESS, FAILED, ROLLED_BACK */
  status: string;
  /** MANUAL, AUTO, FORCE, CI */
  triggerType: string;
  log?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  blocked: boolean;
  blocking: DeployFindingDto[];
}

export type ServerDeploymentPayload = {
  name?: string;
  deployPath?: string;
  domain?: string;
  linkedRepositoryId?: number | null;
  deployBranch?: string;
  deployStrategy?: string;
  healthUrl?: string;
  backupEnabled?: boolean;
  backupService?: string;
  approvalRequired?: boolean;
};

export interface CveJournalIntervention {
  id: number;
  cveId?: string | null;
  packageName?: string | null;
  fromVersion?: string | null;
  toVersion?: string | null;
  reason?: string | null;
  createdByLogin?: string | null;
  repoFullName?: string | null;
  status?: string | null;
  usageCount?: number;
  createdAt?: string | null;
}

export interface CveJournalEntry {
  cveId?: string | null;
  packageName?: string | null;
  severity?: string | null;
  cvssScore?: number | null;
  epssScore?: number | null;
  fixedVersion?: string | null;
  source?: string | null;
  ecosystem?: string | null;
  description?: string | null;
  detectionCount?: number;
  kevListed?: boolean;
  exploitAvailable?: boolean;
  officialStableVersion?: string | null;
  officialComment?: string | null;
  officialUpdatedBy?: string | null;
  officialUpdatedById?: number | null;
  officialUpdatedAt?: string | null;
  guidanceId?: number | null;
  developerInterventions?: CveJournalIntervention[];
  hasDeveloperFix?: boolean;
  hasOfficialGuidance?: boolean;
  remediationStatus?: string | null;
  remediationStatusLabel?: string | null;
  policySource?: "CHEF" | "SCAN" | string | null;
  preferredFixVersion?: string | null;
  firstSeenAt?: string | null;
  closedAt?: string | null;
  daysOpen?: number | null;
  kevOverdue?: boolean;
  kevDateAdded?: string | null;
  kevCisaDueDate?: string | null;
  kevCisaDueSource?: string | null;
  kevCisaOverdue?: boolean;
  kevCisaDaysRemaining?: number | null;
}

export interface CveExposureRepo {
  repositoryId?: number;
  repoName?: string | null;
  repoUrl?: string | null;
  repoFullName?: string | null;
  gitProvider?: string | null;
  branch?: string | null;
  clientName?: string | null;
  packageName?: string | null;
  packageVersion?: string | null;
  filePath?: string | null;
  source?: string | null;
  status?: string | null;
  stillOpen?: boolean;
  kevListed?: boolean;
  kevInternalOverdue?: boolean;
  kevCisaOverdue?: boolean;
  officialStableVersion?: string | null;
  canPropagate?: boolean;
}

export interface CveExposureRow {
  cveId: string;
  severity?: string | null;
  cvssScore?: number | null;
  kevListed?: boolean;
  kevRansomware?: boolean;
  repoCount?: number;
  openRepoCount?: number;
  patchedRepoCount?: number;
  clientCount?: number;
  exposureScore?: number;
  canPropagateCount?: number;
  repositories?: CveExposureRepo[];
}

export interface CvePropagateResponse {
  cveId?: string;
  packageName?: string | null;
  officialStableVersion?: string | null;
  attempted?: number;
  committed?: number;
  skipped?: number;
  failed?: number;
  truncated?: number;
  results?: Array<{
    repositoryId?: number;
    repoFullName?: string | null;
    repoName?: string | null;
    packageName?: string | null;
    fromVersion?: string | null;
    toVersion?: string | null;
    status?: "COMMITTED" | "SKIPPED" | "FAILED" | string;
    reason?: string | null;
    reasonCode?: string | null;
    commitUrl?: string | null;
  }>;
}

export interface CveJournalResponse {
  catalog: CveJournalEntry[];
  interventions: CveJournalIntervention[];
  exposure?: CveExposureRow[];
  stats: {
    totalCves: number;
    withOfficialGuidance: number;
    withDeveloperFix: number;
    interventionCount: number;
    byStatus?: Record<string, number>;
    slaHours?: number;
    cisaDefaultDays?: number;
    meanDaysOpen?: number | null;
    openWithAgeCount?: number;
    kevTotal?: number;
    kevOpenCount?: number;
    kevOverdueCount?: number;
    kevCisaOverdueCount?: number;
    kevClosedWithTimingCount?: number;
    kevFixedWithin24hCount?: number;
    kevFixedWithin24hPercent?: number | null;
  };
}

export interface CveJournalPolicy {
  cveId?: string;
  packageName?: string | null;
  officialStableVersion?: string | null;
  officialComment?: string | null;
  officialUpdatedBy?: string | null;
  guidanceId?: number | null;
  policySource?: "CHEF" | string | null;
}

export interface CveAuditEventDto {
  id?: number | null;
  cveId?: string | null;
  packageName?: string | null;
  eventType?: string | null;
  actorLogin?: string | null;
  fromVersion?: string | null;
  toVersion?: string | null;
  officialVersion?: string | null;
  repoFullName?: string | null;
  message?: string | null;
  createdAt?: string | null;
  expiresAt?: string | null;
  synthetic?: boolean;
}

export interface CveVersionRecommendation {
  cveId?: string | null;
  packageName?: string | null;
  candidates?: string[];
  recommendedVersion?: string | null;
  rationale?: string | null;
  comparedToOthers?: Array<{ version?: string; whyNot?: string }>;
  source?: "LLM" | "HEURISTIC" | "NONE" | "ERROR" | string | null;
  aiProvider?: string | null;
  aiError?: string | null;
}

export interface OfficialGuidanceRequest {
  cveId: string;
  packageName?: string;
  stableVersion: string;
  comment?: string;
}

/** Auth is cookie-based (HttpOnly); keep helper for call sites that pass headers. */
const authHeaders = () => ({});

export const requestFix = (data: FixPreviewRequest) =>
  API.post<FixPreviewResponse>("/autofix/preview", data, {
    headers: authHeaders(),
  });

export const applyFix = (data: FixApplyRequest) =>
  API.post<FixApplyResponse>("/autofix/apply", data, {
    headers: authHeaders(),
  });

export interface VersionValidationResult {
  packageName?: string;
  cveId?: string;
  currentVersion?: string | null;
  recommendedVersion?: string | null;
  chosenVersion?: string | null;
  verdict: "OK" | "RISKY" | "UNKNOWN" | string;
  riskLevel?: "LOW" | "MEDIUM" | "HIGH" | string;
  title?: string;
  summary?: string;
  details?: string[];
  advice?: string;
  canProceed?: boolean;
  source?: string;
}

export const validateFixVersion = (data: {
  packageName: string;
  currentVersion?: string | null;
  recommendedVersion?: string | null;
  chosenVersion?: string | null;
  cveId?: string | null;
  ecosystem?: string | null;
  filePath?: string | null;
  fixedContent?: string | null;
}) =>
  API.post<VersionValidationResult>("/autofix/validate-version", data, {
    headers: authHeaders(),
  });

export const getGithubRepos = () => API.get<GitRepoDto[]>("/auth/github/repos");

export const getGithubLinkUrl = () =>
  API.get<{ url: string }>("/auth/github/link-url");

export const getGitlabProjects = () =>
  API.get<GitRepoDto[]>("/auth/gitlab/projects");

export const getGitlabLinkUrl = () =>
  API.get<{ url: string }>("/auth/gitlab/link-url");

export const linkProviderToken = (
  provider: "GITHUB" | "GITLAB",
  token: string,
  gitlabUrl?: string,
) => API.post<UserDto>("/auth/link-token", { provider, token, gitlabUrl });

export interface ProviderTokenStatus {
  linked: boolean;
  tokenKind?: string;
  maskedToken?: string | null;
  valid?: boolean;
  warning?: string | null;
  error?: string | null;
  githubLogin?: string | null;
  githubName?: string | null;
  scopes?: string | null;
  hasRepoScope?: boolean;
  repoFullName?: string | null;
  canPush?: boolean;
  pushError?: string | null;
  permissions?: Record<string, boolean> | null;
  gitlabUrl?: string | null;
  gitlabLogin?: string | null;
}

export interface TokensStatusResponse {
  github: ProviderTokenStatus;
  gitlab: ProviderTokenStatus;
}

export const getTokensStatus = (repoFullName?: string | null) =>
  API.get<TokensStatusResponse>("/users/me/git-tokens", {
    params: repoFullName ? { repoFullName } : undefined,
  });

export const unlinkGithubToken = () => API.delete<UserDto>("/users/me/github-token");

export const unlinkGitlabToken = () => API.delete<UserDto>("/users/me/gitlab-token");

export const loginWithEmail = (email: string, password: string) =>
  API.post<LocalLoginResponse>("/auth/login", { email, password });

export const getUsers = () => API.get<UserDto[]>("/users");

export const createUser = (data: {
  login: string;
  name: string;
  email: string;
  password: string;
  accessRoleId: number | null;
  role?: string;
}) => API.post<UserDto>("/users", data);

export const updateUser = (
  id: number,
  data: {
    login: string;
    name: string;
    email: string;
    password?: string;
    accessRoleId: number | null;
    role?: string;
  },
) => API.put<UserDto>(`/users/${id}`, data);

export const updateUserRole = (
  id: number,
  accessRoleId: number,
  role?: string,
) => API.put<UserDto>(`/users/${id}/role`, { accessRoleId, role });

export const updateUserSuspension = (id: number, suspended: boolean) =>
  API.put<UserDto>(`/users/${id}/suspension`, { suspended });

export const deleteUser = (id: number) => API.delete(`/users/${id}`);

export const getAccessRoles = () => API.get<AccessRoleDto[]>("/access-roles");

export const createAccessRole = (data: {
  name: string;
  description: string;
  baseRole: string;
  permissions: string[];
}) => API.post<AccessRoleDto>("/access-roles", data);

export const updateAccessRole = (
  id: number,
  data: {
    name: string;
    description: string;
    baseRole: string;
    permissions: string[];
  },
) => API.put<AccessRoleDto>(`/access-roles/${id}`, data);

export const deleteAccessRole = (id: number) =>
  API.delete(`/access-roles/${id}`);

export const getClients = () => API.get<ClientDto[]>("/clients");

export const getServerNodes = () => API.get<ServerNodeDto[]>("/servers");

export const createServerNode = (data: ServerNodeRequest) =>
  API.post<ServerNodeDto>("/servers", data);

export const updateServerNode = (id: number, data: ServerNodeRequest) =>
  API.put<ServerNodeDto>(`/servers/${id}`, data);

export const deleteServerNode = (id: number) => API.delete(`/servers/${id}`);

export const getServerNode = (id: number) =>
  API.get<ServerNodeDetailDto>(`/servers/${id}`);

export const getLiveServerNode = (id: number) =>
  API.post<ServerNodeDetailDto>(`/servers/${id}/live`);

export const scanServerNode = (id: number) =>
  API.post<ServerNodeDetailDto>(`/servers/${id}/scan`);

export const getServerDeployments = (id: number) =>
  API.get<ServerDeploymentDto[]>(`/servers/${id}/deployments`);

export const createServerDeployment = (id: number, data: ServerDeploymentPayload) =>
  API.post<ServerDeploymentDto>(`/servers/${id}/deployments`, data);

export const updateServerDeployment = (id: number, deploymentId: number, data: ServerDeploymentPayload) =>
  API.patch<ServerDeploymentDto>(`/servers/${id}/deployments/${deploymentId}`, data);

export const deleteServerDeployment = (id: number, deploymentId: number) =>
  API.delete(`/servers/${id}/deployments/${deploymentId}`);

export const setDeploymentAutoDeploy = (id: number, deploymentId: number, enabled: boolean) =>
  API.patch<ServerDeploymentDto>(`/servers/${id}/deployments/${deploymentId}/auto-deploy`, { enabled });

export const deployServerDeployment = (id: number, deploymentId: number, force = false) =>
  API.post<DeployRunDto>(`/servers/${id}/deployments/${deploymentId}/deploy`, { force }, { timeout: 12 * 60 * 1000 });

export const getDeploymentRuns = (id: number, deploymentId: number) =>
  API.get<DeployRunDto[]>(`/servers/${id}/deployments/${deploymentId}/runs`);

export const updateServerDeploySettings = (
  id: number,
  data: {
    deployPath?: string;
    domain?: string;
    linkedRepositoryId?: number | null;
    deployBranch?: string;
    deployStrategy?: string;
  },
) => API.patch<ServerNodeDetailDto>(`/servers/${id}/deploy-settings`, data);

export const setServerAutoDeploy = (id: number, enabled: boolean) =>
  API.patch<ServerNodeDetailDto>(`/servers/${id}/auto-deploy`, { enabled });

export const deployServerNode = (id: number, force = false) =>
  API.post<DeployRunDto>(`/servers/${id}/deploy`, { force }, { timeout: 12 * 60 * 1000 });

export const getServerDeploys = (id: number) =>
  API.get<DeployRunDto[]>(`/servers/${id}/deploys`);

/** Génère des recommandations IA pour les ports exposés du dernier scan du serveur */
export const getPortRecommendations = (id: number) =>
  API.post<PortRecommendationDto[]>(`/servers/${id}/port-recommendations`);

export const getServerFindings = (id: number) =>
  API.get<HardeningFindingDto[]>(`/servers/${id}/findings`);

export interface ModelMetric { label: string; value: string; note?: string }

export interface ModelCard {
  id: string;
  name: string;
  /** RELIABLE | IMPROVABLE | THIRD_PARTY | REMOVED */
  status: string;
  version: string;
  kind: string;
  purpose: string;
  usedIn: string;
  how: string;
  dataset: { name: string; size: string; split: string };
  metrics: ModelMetric[];
  limits: string[];
  sources: string[];
  removed: boolean;
  live?: { tracked: boolean; installed: boolean; exportedAt?: string | null };
}

export interface ModelCardsResponse {
  updated?: string | null;
  note?: string | null;
  error?: string | null;
  models: ModelCard[];
}

/** Needs the MODEL_INFO permission (given to roles by the admin). */
export const getModelCards = () => API.get<ModelCardsResponse>("/models");

export const getCveJournal = () =>
  API.get<CveJournalResponse>("/cve-journal");

export interface ReleaseReadinessPriority {
  cveId: string;
  packageName?: string | null;
  severity?: string | null;
  cvssScore?: number | null;
  kevListed?: boolean;
  blocking?: boolean;
  repoCount?: number;
  repositories?: string[];
}

export interface ReleaseReadinessSecret {
  id: number;
  ruleId?: string | null;
  description?: string | null;
  file?: string | null;
  repository?: string | null;
  verdict?: string | null;
}

/** Delivery date versus the open release blockers. No duration estimate: the verdict comes from visible rules. */
export interface ReleaseReadinessDto {
  clientId: number;
  clientName?: string;
  releaseDeadline?: string | null;
  releaseLabel?: string | null;
  daysRemaining?: number | null;
  delayRisk?: "FAIBLE" | "MOYEN" | "ELEVE" | string | null;
  openFindingCount: number;
  blockingCount: number;
  kevBlockingCount?: number;
  scannedRepoCount: number;
  disclaimer?: string | null;
  priorities: ReleaseReadinessPriority[];
  blockingSecrets?: ReleaseReadinessSecret[];
}

export const getReleaseReadiness = (clientId: number) =>
  API.get<ReleaseReadinessDto>(`/clients/${clientId}/release-readiness`);

export const propagateCveFix = (data: {
  cveId: string;
  packageName?: string | null;
  repositoryIds?: number[];
}) => API.post<CvePropagateResponse>("/cve-journal/propagate", data);

export const getCveJournalPolicy = (cveId: string, packageName?: string | null) =>
  API.get<CveJournalPolicy>("/cve-journal/policy", {
    params: { cveId, packageName: packageName ?? "" },
  });

export const getCveJournalTimeline = (cveId: string, packageName?: string | null) =>
  API.get<CveAuditEventDto[]>("/cve-journal/timeline", {
    params: { cveId, packageName: packageName ?? "" },
  });

export const getCveJournalRecommendation = (params: {
  cveId: string;
  packageName?: string | null;
  fixedVersion?: string | null;
  severity?: string | null;
  description?: string | null;
  ecosystem?: string | null;
}) =>
  API.post<CveVersionRecommendation>("/cve-journal/recommend", {
    cveId: params.cveId,
    packageName: params.packageName ?? "",
    fixedVersion: params.fixedVersion ?? "",
    severity: params.severity ?? "",
    description: params.description ?? "",
    ecosystem: params.ecosystem ?? "",
  });

export const upsertOfficialGuidance = (data: OfficialGuidanceRequest) =>
  API.put<{
    id: number;
    cveId: string;
    packageName?: string;
    stableVersion: string;
    comment?: string;
    updatedByLogin?: string;
    updatedAt?: string;
  }>("/cve-journal/official", data);

export const deleteOfficialGuidance = (id: number) =>
  API.delete(`/cve-journal/official/${id}`);

export const recordCveFalsePositive = (data: {
  cveId: string;
  packageName?: string;
  reason: string;
  expiresAt?: string | null;
}) => API.post<CveAuditEventDto>("/cve-journal/false-positive", data);

export const getNotifications = () =>
  API.get<AppNotificationDto[]>("/notifications");

export const getUnreadNotificationCount = () =>
  API.get<{ count: number }>("/notifications/unread-count");

export const markNotificationRead = (id: number) =>
  API.post(`/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  API.post("/notifications/read-all");

export const clearAllNotifications = () =>
  API.post("/notifications/clear-all");

export const getPendingPolicyDeviations = () =>
  API.get<PolicyDeviationDto[]>("/policy-deviations/pending");

export const approvePolicyDeviation = (id: number, comment?: string) =>
  API.post<PolicyDeviationDto>(`/policy-deviations/${id}/approve`, { comment: comment ?? null });

export const rejectPolicyDeviation = (id: number, comment?: string) =>
  API.post<PolicyDeviationDto>(`/policy-deviations/${id}/reject`, { comment: comment ?? null });

export const getClient = (id: number) => API.get<ClientDto>(`/clients/${id}`);

/** What deleting a project removes: repositories / servers used only by it are deleted, shared ones detached. */
export interface ProjectDeletionPreview {
  projectId: number;
  projectName: string;
  repositoriesDeleted: string[];
  repositoriesDetached: string[];
  scans: number;
  serversDeleted: string[];
  serversDetached: string[];
  deployments: number;
  ciRepositories: number;
  /** Scans or deployments in progress: the deletion is refused until they finish. */
  blockers: string[];
}

export const getProjectDeletionPreview = (id: number) =>
  API.get<ProjectDeletionPreview>(`/clients/${id}/deletion-preview`);

/** {@code confirm} must be the project name. */
export const deleteProject = (id: number, confirm: string) =>
  API.delete(`/clients/${id}`, { params: { confirm } });

/** A repository deployed on one of the project's servers. */
export interface ProjectDeploymentDto {
  id: number | null;
  name?: string | null;
  deployPath?: string | null;
  domain?: string | null;
  linkedRepositoryId?: number | null;
  repositoryName?: string | null;
  deployBranch?: string | null;
  deployStrategy?: string | null;
  autoDeployEnabled?: boolean | null;
  lastStatus?: string | null;
  lastCommitSha?: string | null;
}

/** A hardening weakness or a CVE already found on the server itself. */
export interface ProjectServerIssue {
  severity: string;
  title: string;
}

/** A port already listening. unexpected = public, and not 22, 80 or 443. */
export interface ProjectPortUse {
  port: number;
  protocol?: string | null;
  service?: string | null;
  exposure?: string | null;
  unexpected: boolean;
}

/** Last scan of a linked server: flaws and ports already in use. */
export interface ProjectServerPosture {
  scanned: boolean;
  criticalCount: number;
  warningCount: number;
  unexpectedPortCount: number;
  issues: ProjectServerIssue[];
  ports: ProjectPortUse[];
}

/** A server (VPS) attached to a project; a project can run on several. */
export interface ProjectServerDto {
  id: number;
  name: string;
  host: string;
  environment?: string | null;
  nodeType?: string | null;
  /** false for lightweight servers: no SSH access, so no internal analysis. */
  sshConfigured: boolean;
  deployments?: ProjectDeploymentDto[];
  posture?: ProjectServerPosture | null;
}

export const getClientServers = (clientId: number) =>
  API.get<ProjectServerDto[]>(`/clients/${clientId}/servers`);

export const linkClientServer = (clientId: number, serverId: number) =>
  API.post<ProjectServerDto[]>(`/clients/${clientId}/servers`, { serverId });

export const createLightweightClientServer = (
  clientId: number,
  data: { name: string; host: string; environment: "TEST" | "PROD" },
) => API.post<ProjectServerDto[]>(`/clients/${clientId}/servers/lightweight`, data);

// ── Server vulnerability analysis (open ports + system packages) ──
export interface ServerVulnRef {
  id: string;
  cvss: number | null;
  severity: string;
  exploit: boolean;
  kev: boolean;
  fixedVersion?: string | null;
}
export interface ServerPortFinding {
  port: number;
  protocol: string;
  service: string;
  product: string;
  version: string;
  /** EXTERNAL | FIREWALLED | LOCAL_ONLY | UNKNOWN */
  exposure: string;
  vulns: ServerVulnRef[];
}
export interface ServerPackageFinding {
  name: string;
  version: string;
  fixedVersion?: string | null;
  severity: string;
  vulns: ServerVulnRef[];
}
export interface ServerVulnReport {
  host: string;
  os?: string | null;
  externalScan: boolean;
  internalScan: boolean;
  ports: ServerPortFinding[];
  packages: ServerPackageFinding[];
  summary: {
    openPorts: number; exposedPorts: number; vulnerablePorts: number; vulnerablePackages: number;
    critical: number; high: number; medium: number; low: number; kev: number;
  };
  warnings: string[];
}
export interface ServerVulnScanView {
  id: number;
  /** RUNNING | COMPLETED | PARTIAL | FAILED */
  status: string;
  startedAt: string;
  finishedAt?: string | null;
  errorMessage?: string | null;
  report?: ServerVulnReport | null;
}

export const getServerVulnScan = (serverId: number) =>
  API.get<ServerVulnScanView | "">(`/servers/${serverId}/vuln-scan`);

export const startServerVulnScan = (serverId: number, options: { external: boolean; internal: boolean }) =>
  API.post<ServerVulnScanView>(`/servers/${serverId}/vuln-scan`, options, { timeout: 30000 });

export const unlinkClientServer = (clientId: number, serverId: number) =>
  API.delete(`/clients/${clientId}/servers/${serverId}`);

export const createClient = (data: {
  name: string;
  company: string;
  domainName?: string;
  email: string;
  releaseDeadline?: string | null;
  releaseLabel?: string | null;
}) => API.post<ClientDto>("/clients", data);

export const updateClient = (
  id: number,
  data: {
    name: string;
    company: string;
    domainName?: string;
    email: string;
    releaseDeadline?: string | null;
    releaseLabel?: string | null;
  },
) => API.put<ClientDto>(`/clients/${id}`, data);

export const setClientSslAlert = (id: number, alertDate: string | null) =>
  API.put<ClientDto>(`/clients/${id}/ssl-alert`, { alertDate });

export const setClientRepoRole = (
  clientId: number,
  repoId: number,
  role: "FRONT" | "BACK" | "DATABASE" | "OTHER",
) => API.put<ClientDto>(`/clients/${clientId}/repositories/${repoId}/role`, { role });

export const assignEmployeeToClient = (id: number, employeeId: number) =>
  API.post<ClientDto>(`/clients/${id}/assign-employee`, { employeeId });

export const assignRepositoryToClient = (id: number, repositoryId: number) =>
  API.post<ClientDto>(`/clients/${id}/assign-repo`, { repositoryId });

/** Detaches a repository from a project; {@code deleteRepository} also deletes it with its scans (admin, not shared). */
export const removeRepositoryFromClient = (id: number, repoId: number, deleteRepository = false) =>
  API.delete(`/clients/${id}/repos/${repoId}`, { params: { deleteRepository } });

export interface RepoRemovalPreview {
  repositoryId: number;
  repository: string;
  /** Also linked to another project: it can only be detached. */
  shared: boolean;
  /** Administrator and not shared: may be deleted with its scans. */
  canDelete: boolean;
  scans: number;
  ciEnabled: boolean;
  blockers: string[];
}

export const getRepoRemovalPreview = (id: number, repoId: number) =>
  API.get<RepoRemovalPreview>(`/clients/${id}/repos/${repoId}/removal-preview`);

export const listCiTokens = (clientId: number) =>
  API.get<CiTokenDto[]>("/admin/ci-tokens", { params: { clientId } });

export const createCiToken = (data: CreateCiTokenRequest) =>
  API.post<CiTokenCreatedDto>("/admin/ci-tokens", data);

export const revokeCiToken = (id: number) =>
  API.delete<CiTokenDto>(`/admin/ci-tokens/${id}`);

export const revealCiToken = (id: number) =>
  API.get<CiTokenCreatedDto>(`/admin/ci-tokens/${id}/secret`);

export const deleteCiTokenPermanently = (id: number) =>
  API.delete(`/admin/ci-tokens/${id}/permanent`);

// ── SSL Analysis ─────────────────────────────────────────────────────────────

export type TlsProtocolStatus = 'ENABLED' | 'DISABLED' | 'NOT_TESTED' | 'INCONCLUSIVE';
export type TlsCipherStrength = 'STRONG' | 'WEAK' | 'FORBIDDEN';

export interface TlsCipherSuiteDto {
  ianaName: string;
  opensslName?: string;
  encryption?: string;
  keyExchange?: string;
  keySize?: number;
  forwardSecrecy: boolean;
  aead: boolean;
  strength: TlsCipherStrength;
}

export interface TlsProtocolDetailDto {
  id: string;
  label: string;
  status: TlsProtocolStatus;
  handshakeOk?: boolean | null;
  acceptedCount?: number;
  weakCount?: number;
  forbiddenCount?: number;
  forwardSecrecy?: boolean | null;
  aead?: boolean | null;
  compression?: boolean | null;
  secureRenegotiation?: boolean | null;
  endpoint?: string | null;
  ip?: string | null;
  port?: number | null;
  sni?: string | null;
  tool?: string | null;
  toolVersion?: string | null;
  scannedAt?: string | null;
  confidence?: string | null;
  evidence?: string | null;
  ciphers?: TlsCipherSuiteDto[];
}

export interface CertNameDto {
  commonName?: string | null;
  organization?: string | null;
  country?: string | null;
  countryName?: string | null;
  rfc4514?: string | null;
}

export interface CertSanEntryDto {
  type: string;
  value: string;
  matchStatus?: string | null;
}

export interface CertChainEntryDto {
  type: string;
  subject?: CertNameDto | null;
  issuer?: CertNameDto | null;
  serialNumber?: string | null;
  notAfter?: string | null;
  signatureAlgorithm?: string | null;
  sha256Fingerprint?: string | null;
  status?: string | null;
  pem?: string | null;
}

export interface CertTrustStoreDto {
  platform: string;
  status: string;
  storeVersion?: string | null;
  validationError?: string | null;
}

export interface CertificateDetailDto {
  validityStatus?: string | null;
  notBefore?: string | null;
  notAfter?: string | null;
  totalValidityDays?: number | null;
  daysRemaining?: number | null;
  percentRemaining?: number | null;
  recommendedRenewalDate?: string | null;
  expired?: boolean | null;
  commonName?: string | null;
  testedHostname?: string | null;
  hostnameMatch?: string | null;
  wildcard?: boolean | null;
  sans?: CertSanEntryDto[];
  publicKeyAlgorithm?: string | null;
  keyType?: string | null;
  keySize?: number | null;
  curveName?: string | null;
  signatureAlgorithm?: string | null;
  hashAlgorithm?: string | null;
  securityLevel?: string | null;
  weakKey?: boolean | null;
  obsoleteSignature?: boolean | null;
  chainComplete?: boolean | null;
  chainOrderValid?: boolean | null;
  intermediatePresent?: boolean | null;
  rootRecognized?: boolean | null;
  selfSigned?: boolean | null;
  validationError?: string | null;
  chain?: CertChainEntryDto[];
  ocspUrlStatus?: string | null;
  ocspUrl?: string | null;
  ocspResponseStatus?: string | null;
  revocationStatus?: string | null;
  ocspStaplingStatus?: string | null;
  crlUrlStatus?: string | null;
  crlUrl?: string | null;
  transparencyStatus?: string | null;
  sctCount?: number | null;
  ctLogs?: string | null;
  mustStaple?: boolean | null;
  keyUsage?: string | null;
  extendedKeyUsage?: string | null;
  serverAuth?: boolean | null;
  clientAuth?: boolean | null;
  basicConstraints?: string | null;
  isCa?: boolean | null;
  trustStores?: CertTrustStoreDto[];
  endpoint?: string | null;
  ip?: string | null;
  port?: number | null;
  sni?: string | null;
  scannedAt?: string | null;
  scanDuration?: string | null;
  tool?: string | null;
  toolVersion?: string | null;
  confidence?: string | null;
  sha256Fingerprint?: string | null;
  serialNumber?: string | null;
  leafPem?: string | null;
  ev?: boolean | null;
}

export interface SslResultDto {
  domain: string;
  grade: string;
  scanStatus: string;
  source: string;
  // Protocols
  tls10: boolean;
  tls11: boolean;
  tls12: boolean;
  tls13: boolean;
  /** Detailed matrix from SSLyze (or Kali fallback) — 6 entries when present */
  tlsProtocols?: TlsProtocolDetailDto[];
  certificateDetail?: CertificateDetailDto | null;
  // Vulnerabilities
  heartbleed: boolean;
  /** Preuve brute Heartbleed (sslscan / nmap / testssl). */
  heartbleedEvidence?: string | null;
  sweet32: boolean;
  has3des: boolean;
  crime: boolean;
  poodle: boolean;
  beast: boolean;
  robot: boolean;
  freak: boolean;
  logjam: boolean;
  rc4: boolean;
  drown: boolean;
  // Certificate
  certExpired: boolean;
  certDaysLeft: number;
  certIssuer: string;
  certSubject: string;
  chainComplete: boolean;
  certSignatureAlg: string;
  certKeySize: string;
  certNotBefore: string;
  certNotAfterStr: string;
  certSerialNumber: string;
  certEv: boolean;
  certWildcard: boolean;
  certTransparency: boolean;
  certSansCount: number;
  // Headers
  hsts: boolean;
  ocspStapling: boolean;
  xFrameOptions: boolean;
  xContentTypeOptions: boolean;
  contentSecurityPolicy: boolean;
  /** Present when only CSP-Report-Only is set (does not block XSS). */
  cspReportOnly?: boolean;
  referrerPolicy: boolean;
  permissionsPolicy: boolean;
  crossOriginOpenerPolicy?: boolean;
  crossOriginResourcePolicy?: boolean;
  crossOriginEmbedderPolicy?: boolean;
  hstsValue?: string | null;
  cspValue?: string | null;
  xFrameOptionsValue?: string | null;
  xContentTypeOptionsValue?: string | null;
  referrerPolicyValue?: string | null;
  permissionsPolicyValue?: string | null;
  crossOriginOpenerPolicyValue?: string | null;
  crossOriginResourcePolicyValue?: string | null;
  crossOriginEmbedderPolicyValue?: string | null;
  headersCheckedUrl?: string | null;
  headersHttpStatus?: number | null;
  headersLiveChecked?: boolean;
  /** Web server from the live "Server" header: "nginx", "apache", another name, or null. */
  webServer?: string | null;
  // SSL Labs external scan
  ssllabsGrade: string;
  ssllabsStatus: string; // 'PENDING' | 'READY' | 'ERROR' | 'TIMEOUT' | 'DISABLED'
  ssllabsIpAddress: string;
  ssllabsHasWarnings: boolean;
  ssllabsForwardSecrecy: boolean;
  ssllabsDrown: boolean;
  // Censys Platform API
  censysGrade: string;
  censysStatus: string; // 'PENDING' | 'READY' | 'ERROR' | 'DISABLED'
  censysIpAddress: string;
  censysDaysLeft: number;
  censysExpired: boolean;
  censysCertValid: boolean;
  censysIssuer: string;
  censysKeySize: string;
  censysValidationLevel: string; // 'DV' | 'OV' | 'EV'
  censysCtPresent: boolean;
  censysSansCount: number;
  censysOpenPorts: string;
  // SSLyze (local parse of sslyze.json from Kali scan)
  sslyzeGrade: string;
  sslyzeStatus: string; // 'PENDING' | 'READY' | 'ERROR'
  sslyzeIpAddress: string;
  sslyzeSupportsSSL20: boolean;
  sslyzeSupportsSSL30: boolean;
  sslyzeSupportsTLS10: boolean;
  sslyzeSupportsTLS11: boolean;
  sslyzeSupportsTLS12: boolean;
  sslyzeSupportsTLS13: boolean;
  sslyzeHeartbleed: boolean;
  sslyzeRobot: boolean;
  sslyzeCcsInjection: boolean;
  sslyzeCompression: boolean;
  sslyzeInsecureRenegotiation: boolean;
  sslyzeCertSubject: string;
  sslyzeCertIssuer: string;
  sslyzeKeySize: number;
  sslyzeChainTrusted: boolean;
  sslyzeOcspStapling: boolean;
  sslyzeDaysLeft: number;
  sslyzeCipherCount: number;
  sslyzeVersion?: string | null;
  sslyzeScanStarted?: string | null;
  sslyzeSni?: string | null;
  sslyzePort?: number | null;
  // SSL Labs details (all server IPs, real 3DES check, age of the assessment)
  ssllabsHas3des?: boolean;
  ssllabsTestTime?: string | null;
  ssllabsFromCache?: boolean;
  ssllabsEndpoints?: Array<{ ipAddress: string; grade: string; statusMessage: string }>;
  ssllabsError?: string | null;
  censysError?: string | null;
  // Scan context
  scanStartedAt?: string | null;
  scanFinishedAt?: string | null;
  targetPort?: number | null;
  /** Problems met while reading the results (unreadable file…), shown on the page. */
  warnings?: string[];
  // Tools' summary (backend SslGradeCalculator.combine) — the page verdict is the category score
  combinedGrade: string;
  sourcesReady: number;
  sourcesTotal: number;
}

/** Per finished scan of a repository: unique vulnerabilities by severity, code alerts, secrets (Évolution tab). */
export const getRepositoryHistory = (repoId: number) =>
  API.get<import("../utils/evolution").HistoryRow[]>(`/repositories/${repoId}/history`);

export const startSslScan = (domain: string) =>
  API.post<ScanResponse>("/ssl/scan", { domain });

/** Stops the Kali container and the SSL Labs / Censys lookups of an SSL scan. */
export const cancelSslScan = (scanId: number) => API.post(`/ssl/scan/${scanId}/cancel`);

/** Last finished SSL scan of the same target before this one (204 when there is none). */
export const getPreviousSslResult = (scanId: number) =>
  API.get<{ scanId: number; startedAt: string; result: SslResultDto } | "">(`/ssl/scan/${scanId}/previous`);

export const getSslResult = (scanId: number) =>
  API.get<SslResultDto>(`/ssl/scan/${scanId}/result`).then(res => {
    // Jackson may emit xframeOptions / xcontentTypeOptions without @JsonProperty
    const d = res.data as SslResultDto & {
      xframeOptions?: boolean;
      xcontentTypeOptions?: boolean;
    };
    if (d.xFrameOptions === undefined && d.xframeOptions !== undefined) {
      d.xFrameOptions = d.xframeOptions;
    }
    if (d.xContentTypeOptions === undefined && d.xcontentTypeOptions !== undefined) {
      d.xContentTypeOptions = d.xcontentTypeOptions;
    }
    return res;
  });

// ── AI Summary ────────────────────────────────────────────────────────────────

export const getAiSummary = (scanId: number) =>
  API.get<{ summary: string }>(`/scans/${scanId}/ai-summary`);

export const getSslAiAnalysis = (context: Record<string, unknown>) =>
  API.post<{ summary: string; keyRisks: string[]; recommendations: string[] }>(
    "/ssl/ai-analysis",
    context,
  );

// ── Scheduled Scans ──────────────────────────────────────────────────────────

export type ScheduleType = "ONCE" | "WEEKLY" | "EVERY_15_DAYS" | "MONTHLY";
export type ScheduledScanStatus =
  | "ACTIVE"
  | "PAUSED"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED";

export interface ScheduledScan {
  id: number;
  repositoryId: number;
  repositoryName: string;
  repoUrl: string;
  branch?: string;
  scanMode: string;
  targetDomain?: string;
  dastTargetUrl?: string;
  scheduleType: ScheduleType;
  startAt: string;
  nextRunAt: string;
  lastRunAt?: string;
  timezone: string;
  status: ScheduledScanStatus;
  enabled: boolean;
  runCount: number;
  lastScanId?: number;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledScanCreateRequest {
  repositoryId?: number;
  repositoryName: string;
  repoUrl: string;
  branch?: string;
  scanMode: string;
  targetDomain?: string;
  dastTargetUrl?: string;
  scheduleType: ScheduleType;
  startAt: string; // "yyyy-MM-ddTHH:mm:ss"
  timezone: string; // IANA e.g. "Africa/Tunis"
}

export const createScheduledScan = (data: ScheduledScanCreateRequest) =>
  API.post<ScheduledScan>("/scheduled-scans", data);

export const getScheduledScans = () =>
  API.get<ScheduledScan[]>("/scheduled-scans");

export const getRepositoryScheduledScans = (repositoryId: number) =>
  API.get<ScheduledScan[]>(`/repositories/${repositoryId}/scheduled-scans`);

export const getScheduledSummary = () =>
  API.get<Record<string, ScheduledScan>>("/repositories/scheduled-summary");

export const updateScheduledScan = (
  id: number,
  data: Partial<ScheduledScanCreateRequest>,
) => API.put<ScheduledScan>(`/scheduled-scans/${id}`, data);

export const pauseScheduledScan = (id: number) =>
  API.patch<ScheduledScan>(`/scheduled-scans/${id}/pause`);

export const resumeScheduledScan = (id: number) =>
  API.patch<ScheduledScan>(`/scheduled-scans/${id}/resume`);

export const deleteScheduledScan = (id: number) =>
  API.delete(`/scheduled-scans/${id}`);

// ── AI Settings ──────────────────────────────────────────────────────────────

export interface AiSettingsRequest {
  aiProvider: string; // GEMINI | CLAUDE | OPENAI | GROK | GROQ
  aiModel: string;
  aiApiKey: string;
}

export interface ChatAiSettingsRequest {
  chatAiProvider: string;
  chatAiModel: string;
  chatAiApiKey: string;
}

export const updateAiSettings = (data: AiSettingsRequest) =>
  API.patch<UserDto>("/users/me/ai-settings", data);

export const clearAiSettings = () =>
  API.delete<UserDto>("/users/me/ai-settings");

export const updateChatAiSettings = (data: ChatAiSettingsRequest) =>
  API.patch<UserDto>("/users/me/chat-ai-settings", data);

export const clearChatAiSettings = () =>
  API.delete<UserDto>("/users/me/chat-ai-settings");

// ── Assistant Vulnix ─────────────────────────────────────────────────────────

export interface AssistantChatTurn {
  role: "user" | "assistant";
  content: string;
}

export interface AssistantLinkDto {
  label: string;
  href: string;
}

export interface AssistantChatRequest {
  message: string;
  page?: string;
  scanId?: number;
  serverId?: number;
  history?: AssistantChatTurn[];
}

export interface AssistantChatResponse {
  reply: string;
  contextLabel: string;
  links: AssistantLinkDto[];
  usedAi: boolean;
}

export const chatWithAssistant = (data: AssistantChatRequest) =>
  API.post<AssistantChatResponse>("/assistant/chat", data);

export interface AssistantStatusDto {
  available: boolean;
  provider?: string;
  detail?: string;
}

export const getAssistantStatus = () =>
  API.get<AssistantStatusDto>("/assistant/status");

export default API;

// ── CI (Woodpecker, piloté depuis Vulnix) ────────────────────────────────────

export interface RepoCi {
  repositoryId: number;
  repoUrl: string;
  forgeFullName: string | null;
  supported: boolean;
  unsupportedReason: string | null;
  enabled: boolean;
  enabledAt: string | null;
  branch: string;
}

export interface ProjectCi {
  configured: boolean;
  repositories: RepoCi[];
}

export interface PipelineSummary {
  number: number;
  status: string;
  event: string;
  branch: string;
  commit: string;
  message: string;
  author: string;
  /** Unix seconds. */
  created: number | null;
  started: number | null;
  finished: number | null;
}

export interface PipelineStep {
  id: number;
  name: string;
  state: string;
  exitCode: number | null;
  error: string | null;
  started: number | null;
  finished: number | null;
}

export interface PipelineDetail {
  pipeline: PipelineSummary;
  steps: PipelineStep[];
  errors: string[];
}

export interface PipelineLogLine {
  line: number;
  text: string;
}

export const getProjectCi = (clientId: number) => API.get<ProjectCi>(`/projects/${clientId}/ci`);
export const enableRepoCi = (clientId: number, repoId: number) =>
  API.post<RepoCi>(`/projects/${clientId}/ci/repositories/${repoId}`);
export const disableRepoCi = (clientId: number, repoId: number) =>
  API.delete(`/projects/${clientId}/ci/repositories/${repoId}`);
export const getPipelines = (repoId: number) => API.get<PipelineSummary[]>(`/pipelines/repositories/${repoId}`);
export const runPipeline = (repoId: number) => API.post<PipelineSummary>(`/pipelines/repositories/${repoId}`);
export const getPipeline = (repoId: number, number: number) =>
  API.get<PipelineDetail>(`/pipelines/repositories/${repoId}/${number}`);
export const cancelPipeline = (repoId: number, number: number) =>
  API.post<PipelineSummary>(`/pipelines/repositories/${repoId}/${number}/cancel`);
export const getPipelineStepOutput = (repoId: number, number: number, stepId: number) =>
  API.get<PipelineLogLine[]>(`/pipelines/repositories/${repoId}/${number}/steps/${stepId}/output`);

export interface CiRepoPart {
  kind: "maven" | "npm" | "python" | "php";
  dir: string;
  /** Pipeline step name written by Vulnix (maven-backend, npm-frontend…). */
  step: string;
  stack: string;
  hasTests: boolean;
  testFiles: number;
  missing: string | null;
  commands: string[];
  targets: string[];
  /** How the AI must write this part's tests. */
  rules: string[];
  /** Problems that turn the step red whatever the tests (e.g. lint calls eslint, not installed). */
  warnings: string[];
}

export interface CiRepoAnalysis {
  parts: CiRepoPart[];
  plannedSteps: string[];
  /** Text to paste in an AI assistant so it writes the missing tests (paths only, no source code). */
  prompt: string;
}

/** steps: only these parts in the prompt; focus: theme matched against paths ("auth"). */
export const getRepoCiAnalysis = (clientId: number, repoId: number, scope?: { steps?: string[]; focus?: string }) =>
  API.get<CiRepoAnalysis>(`/projects/${clientId}/ci/repositories/${repoId}/analysis`, {
    params: { steps: scope?.steps?.length ? scope.steps.join(",") : undefined, focus: scope?.focus?.trim() || undefined },
  });

// ── CD : où le dépôt est déployé et ce qui tourne ───────────────────────────

export type CdRunStatus =
  | "BLOCKED" | "RUNNING" | "SUCCESS" | "FAILED" | "ROLLED_BACK"
  | "BACKUP_FAILED" | "PENDING_APPROVAL" | "REFUSED" | "SUPERSEDED";

export interface CdRun {
  id: number;
  status: CdRunStatus;
  trigger: "MANUAL" | "AUTO" | "FORCE" | "CI";
  commitSha: string | null;
  pipelineNumber: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  logTail: string;
  /** Database dump taken right before this deploy (path on the server). */
  backupFile: string | null;
  backupBytes: number | null;
  decidedBy: string | null;
}

export interface CdTarget {
  deploymentId: number;
  serverId: number;
  serverName: string;
  host: string;
  path: string | null;
  branch: string | null;
  domain: string | null;
  strategy: string;
  healthUrl: string | null;
  automatic: boolean;
  backupEnabled: boolean;
  backupService: string | null;
  approvalRequired: boolean;
  last: CdRun | null;
  inProduction: CdRun | null;
  /** Green pipeline waiting for "Déployer". */
  pending: CdRun | null;
  /** Last runs that took a database backup (newest first). */
  backups: CdRun[];
  /** Version chosen by hand: green pipelines no longer deploy here until "Reprendre". */
  pinnedCommit?: string | null;
  pinnedAt?: string | null;
  pinnedBy?: string | null;
}

/** Deploys a version chosen by hand (admin); it is pinned when it passes the health check. */
export const deployChosenCommit = (deploymentId: number, commitSha: string) =>
  API.post<DeployRunDto>(`/deployments/${deploymentId}/deploy-commit`, { commitSha });

/** Resumes the automatic deployment of a pinned version. */
export const unpinDeployment = (deploymentId: number) => API.post(`/deployments/${deploymentId}/unpin`);

export const getRepoDeployments = (repoId: number) =>
  API.get<CdTarget[]>(`/pipelines/repositories/${repoId}/deployments`);

export const approveDeployRun = (runId: number) => API.post(`/deploy-runs/${runId}/approve`);
export const refuseDeployRun = (runId: number) => API.post(`/deploy-runs/${runId}/refuse`);

export interface DiskReport {
  serverId: number;
  measuredAt: string;
  diskTotal: number;
  diskUsed: number;
  diskFree: number;
  usedPercent: number;
  oldImages: number;
  oldImagesBytes: number;
  /** Build cache beyond the 2 GB kept for fast rebuilds. */
  buildCacheBytes: number;
  reclaimableBytes: number;
  images: { id: string; repository: string; age: string; bytes: number }[];
  lastCleanupAt: string | null;
  lastCleanupFreed: string | null;
  alert: boolean;
}

/** Last measurement (no SSH); 204 when the server was never measured. */
export const getDeploymentDisk = (deploymentId: number) => API.get<DiskReport | "">(`/deployments/${deploymentId}/disk`);
export const measureDeploymentDisk = (deploymentId: number) => API.post<DiskReport>(`/deployments/${deploymentId}/disk/measure`);
export const cleanDeploymentDisk = (deploymentId: number) =>
  API.post<DiskReport>(`/deployments/${deploymentId}/disk/clean`, { confirm: true });

export const setEmailNotifications = (enabled: boolean) =>
  API.put<{ enabled: boolean }>("/users/me/email-notifications", { enabled });

export interface UptimeMonitor {
  id: number;
  name: string;
  status: "UP" | "DOWN" | "PENDING" | "MAINTENANCE" | "UNKNOWN";
  /** 0..1 over the last 24 h. */
  uptime24h: number | null;
  lastCheck: string | null;
  message: string | null;
}

export interface ProjectUptime {
  configured: boolean;
  reachable: boolean;
  domains: string[];
  monitors: UptimeMonitor[];
}

export const getProjectUptime = (clientId: number) => API.get<ProjectUptime>(`/projects/${clientId}/uptime`);

export interface DeployCheck {
  ok: boolean;
  items: { level: "OK" | "KO" | "WARN"; text: string }[];
  raw: string;
}

/** Read-only check of a deployment on its server: nothing is changed. */
export const checkDeployment = (deploymentId: number) => API.post<DeployCheck>(`/deployments/${deploymentId}/check`);

export const sendTestEmail = (to?: string) =>
  API.post<{ sent: boolean; to: string; reason?: string }>("/users/me/test-email", to ? { to } : {});

// ── Commits of a project's repositories ──

export interface CommitInfo {
  sha: string;
  shortSha: string;
  title: string;
  message: string;
  authorName: string | null;
  authorEmail: string | null;
  authorLogin: string | null;
  date: string | null;
  url: string | null;
}

export interface CommitFileChange { path: string; status: string; additions: number; deletions: number }

export interface CommitDetail {
  commit: CommitInfo;
  additions: number;
  deletions: number;
  files: CommitFileChange[];
  truncated: boolean;
}

export interface RepoCommits { repositoryId: number; repository: string; branch: string | null; commits: CommitInfo[] }

export interface ActivityCommit {
  repositoryId: number;
  repository: string;
  sha: string;
  shortSha: string;
  title: string;
  authorName: string | null;
  authorEmail: string | null;
  authorLogin: string | null;
  date: string | null;
  url: string | null;
}

export interface MemberActivity {
  userId: number;
  name: string | null;
  email: string | null;
  login: string | null;
  commits: number;
  lastCommit: string | null;
  repositories: string[];
  list: ActivityCommit[];
}

export interface OtherAuthor {
  name: string | null;
  email: string | null;
  login: string | null;
  commits: number;
  lastCommit: string | null;
  list: ActivityCommit[];
}

export interface ProjectActivity {
  days: number;
  totalCommits: number;
  members: MemberActivity[];
  others: OtherAuthor[];
  errors: { repositoryId: number; repository: string; message: string }[];
}

/** days: 7, 30, 90 or 0 for the whole history. */
export const getProjectActivity = (clientId: number, days: number) =>
  API.get<ProjectActivity>(`/clients/${clientId}/activity`, { params: { days } });

export const getRepoCommits = (clientId: number, repoId: number, limit = 30) =>
  API.get<RepoCommits>(`/clients/${clientId}/repos/${repoId}/commits`, { params: { limit } });

export const getCommitDetail = (clientId: number, repoId: number, sha: string) =>
  API.get<CommitDetail>(`/clients/${clientId}/repos/${repoId}/commits/${encodeURIComponent(sha)}`);
